import React, { useEffect, useState } from 'react';
import {
    Box,
    Typography,
    IconButton,
    CircularProgress,
    Alert,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    Tooltip,
    Divider,
} from '@mui/material';
import { Refresh, Edit, Add, Info, Description, Gavel } from '@mui/icons-material';
import { getCmsByType } from '../../services/Api';
import { useNavigate } from 'react-router-dom';

const GetAllCms = () => {
    const [cmsData, setCmsData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [type, setType] = useState('about_us');
    const navigate = useNavigate();

    const fetchCmsData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await getCmsByType(type);
            if (response.success) {
                setCmsData(response.data);
            } else {
                setError(`Failed to fetch ${type.replace(/_/g, ' ')} content`);
            }
        } catch (err) {
            setError(`An error occurred while fetching ${type.replace(/_/g, ' ')} content`);
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCmsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type]);

    const getTypeIcon = (t) => {
        switch (t) {
            case 'about_us': return <Info fontSize="small" />;
            case 'terms_and_conditions': return <Gavel fontSize="small" />;
            case 'privacy_policy': return <Description fontSize="small" />;
            default: return <Description fontSize="small" />;
        }
    };

    return (
        <Box>
            {/* Header Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 300, textTransform: 'capitalize' }}>
                        CMS Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage your app's static content like About Us, Terms and Privacy Policy
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <FormControl sx={{ minWidth: 200 }} size="small">
                        <InputLabel id="cms-type-label">Content Type</InputLabel>
                        <Select
                            labelId="cms-type-label"
                            id="cms-type-select"
                            value={type}
                            label="Content Type"
                            onChange={(e) => setType(e.target.value)}
                            sx={{ backgroundColor: 'white' }}
                        >
                            <MenuItem value="about_us">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Info fontSize="small" /> About Us
                                </Box>
                            </MenuItem>
                            <MenuItem value="terms_and_conditions">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Gavel fontSize="small" /> Terms & Conditions
                                </Box>
                            </MenuItem>
                            <MenuItem value="privacy_policy">
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Description fontSize="small" /> Privacy Policy
                                </Box>
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <IconButton onClick={fetchCmsData} disabled={loading} sx={{ backgroundColor: 'rgba(120, 81, 169, 0.1)', color: 'primary.main' }}>
                        {loading ? <CircularProgress size={24} color="inherit" /> : <Refresh />}
                    </IconButton>
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => navigate('/cms/modify')}
                        sx={{
                            backgroundColor: 'primary.main',
                            '&:hover': { backgroundColor: 'primary.dark' },
                            px: 3,
                        }}
                    >
                        Create New
                    </Button>
                </Box>
            </Box>

            {/* Content Section */}
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <CircularProgress color="primary" />
                </Box>
            ) : error ? (
                <Alert severity="error" sx={{ mb: 4 }}>
                    {error}
                </Alert>
            ) : !cmsData ? (
                <Paper
                    elevation={0}
                    sx={{
                        p: 8,
                        textAlign: 'center',
                        backgroundColor: 'rgba(0,0,0,0.02)',
                        border: '2px dashed',
                        borderColor: 'text.border',
                        borderRadius: 4,
                    }}
                >
                    <Description sx={{ fontSize: 60, color: 'text.secondary', opacity: 0.3, mb: 2 }} />
                    <Typography variant="h6" color="text.secondary">
                        No content found for {type.replace(/_/g, ' ')}
                    </Typography>
                    <Button
                        variant="outlined"
                        onClick={() => navigate(`/cms/modify?type=${type}`)}
                        sx={{ mt: 2 }}
                    >
                        Create Content
                    </Button>
                </Paper>
            ) : (
                <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: '1px solid', borderColor: 'text.border' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ color: 'primary.main' }}>
                                {getTypeIcon(type)}
                            </Box>
                            <Typography variant="h6" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>
                                {type.replace(/_/g, ' ')}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Tooltip title="Edit Content">
                                <Button
                                    variant="outlined"
                                    startIcon={<Edit />}
                                    size="small"
                                    onClick={() => navigate(`/cms/modify/${type}`, { state: { cmsData } })}
                                >
                                    Edit
                                </Button>
                            </Tooltip>
                        </Box>
                    </Box>
                    <Divider sx={{ mb: 3 }} />
                    <Box 
                        sx={{ 
                            color: 'text.primary',
                            lineHeight: 1.8,
                            maxWidth: '100%',
                            overflowWrap: 'break-word',
                            wordBreak: 'break-word',
                            '& p': { mb: 2 },
                            '& h1, & h2, & h3': { mt: 3, mb: 1, wordBreak: 'break-word' },
                            '& ul, & ol': { pl: 4, mb: 2 },
                            '& img': { maxWidth: '100%', height: 'auto' }
                        }}
                        dangerouslySetInnerHTML={{ __html: cmsData.content || '' }}
                    />
                </Paper>
            )}
        </Box>
    );
};

export default GetAllCms;
